import { Injectable } from '@angular/core';
import { TimeProvider } from './time-provider';
import { NavManager } from './nav-manager';
import { ITimeGranularityRequestParams } from '../common-components/granularity-selector/time-granularity-types';
import { ShiftProvider } from './shift-provider';
import { TUserLoggedInInfo, UnifiedLoginService } from '@monorepo/services';

/*
 * This service is responsible for providing all the values for runtime filters
 * The runtime values are going to change based on
 * 1. Global date changes
 * 2. Left navigation / org node changes
 * */

@Injectable({
  providedIn: 'root',
})
export class FilterProvider {
  selectedNode: any;
  selectedNodeName: any;
  public selectedItems: any;
  public selectedChildrens: any;
  public oldNode: any;

  constructor(
    private timePro: TimeProvider,
    private unifiedLoginService: UnifiedLoginService,
    private navigation: NavManager,
    private shiftProvider: ShiftProvider,
  ) {}

  setSelectedNode(nodes) {
    this.selectedNode = nodes;
  }

  getSelectedNode() {
    return this.selectedNode;
  }

  getNodeDisplayName() {
    return this.selectedNode['@display_name'] ? this.selectedNode['@display_name'] : '';
  }

  getNodeChildDisplayName() {
    return this.selectedNode['@child_display_name'] ? this.selectedNode['@child_display_name'] : '';
  }

  setOldNodes(oldNode: any) {
    this.oldNode = oldNode;
  }

  getOldNode() {
    return this.oldNode ? this.oldNode : {};
  }

  getChildSplitter() {
    return this.selectedNode['@child_subject_splitter'] ? this.selectedNode['@child_subject_splitter'] : 0;
  }

  getChildDisplayName() {
    return this.selectedNode['@display_name'];
  }

  /*
   * Implement this method to return subject key for current user as well as
   * consider runtime change of subject key scenario as well (teodore)
   * */
  getSubjectKey() {
    if (this.selectedNode['sk']) {
      return this.selectedNode['sk'];
    } else {
      return this.unifiedLoginService.getUserInfo().factoryId;
    }
  }

  setSelectedNodeName(name) {
    this.selectedNodeName = name;
  }

  getSelectedNodeName() {
    return this.selectedNodeName;
  }

  getDatasource() {
    const app = this.navigation.getAppConf();
    const selectedNode = this.getSelectedNode();

    if (selectedNode && selectedNode.hasOwnProperty('ds')) {
      return selectedNode['ds'];
    }
    return app['ds'];
  }

  /*
   * This method should be the centralized parameter filling place, and all the views should use this
   * TODO: remove specific args processing methods from Views
   * 1. time filters should take from time provider
   * 2. other global filter values should have in this service
   * 3. global filter processing should be doing here
   * */
  requestArgumentProcess(
    item: any,
    timeGranularityParamsOpt?: ITimeGranularityRequestParams,
    windowIncrease?: boolean,
  ) {
    const itemCopy = { ...item };
    switch (itemCopy.key) {
      case '@ds': {
        const ds = this.getDatasource();
        itemCopy['value'] = ds;
        break;
      }
      case '@dataSource': {
        // TODO: should remove this and change everything to @ds
        const ds = this.getDatasource();
        itemCopy['value'] = ds;
        break;
      }
      case '@window': {
        // WindowIncrease for multiple shift moved to backend
        if (itemCopy['type'] === 'compareWeek') {
          itemCopy['value'] = this.timePro.setTwoWeeks();
        } else {
          itemCopy['value'] = this.timePro.getWindow(
            itemCopy['n'], // n mean number of dates need to add for window buffer
          );
        }
        break;
      }
      case '@granularity': {
        if (itemCopy['exact']) {
          if (timeGranularityParamsOpt) {
            itemCopy['value'] = timeGranularityParamsOpt.timeGranularity;
          } else {
            const millisecond = new Date(this.timePro.startDate).getTime() - new Date(this.timePro.endDate).getTime();
            const days = Math.round(Math.abs(millisecond) / (1000 * 60 * 60 * 24));
            if (days === 1) {
              itemCopy['value'] = 'all';
            } else if (days > 1 && days <= 28) {
              itemCopy['value'] = 'day';
            } else if (days > 28 && days <= 60) {
              itemCopy['value'] = 'week';
            } else if (days > 60) {
              itemCopy['value'] = 'month';
            }
          }
        }
        break;
      }
      case '@dim1': {
        if (itemCopy['time_block']) {
          if (timeGranularityParamsOpt) {
            itemCopy['value'] = timeGranularityParamsOpt.dim1;
          } else {
            const millisecond = new Date(this.timePro.startDate).getTime() - new Date(this.timePro.endDate).getTime();
            const days = Math.round(Math.abs(millisecond) / (1000 * 60 * 60 * 24));
            if (days === 1) {
              itemCopy['value'] = 'sessionBlock';
            } else if (days > 1 && days <= 28) {
              itemCopy['value'] = 'sessionDate';
            } else if (days > 28) {
              itemCopy['value'] = '@dim1';
            }
          }
        }
        break;
      }
      case '@time_block1': {
        if (itemCopy['granularity']) {
          if (timeGranularityParamsOpt) {
            itemCopy['value'] = timeGranularityParamsOpt.timeBlock1;
          } else {
            const millisecond = new Date(this.timePro.startDate).getTime() - new Date(this.timePro.endDate).getTime();
            const days = Math.round(Math.abs(millisecond) / (1000 * 60 * 60 * 24));
            if (days <= 28) {
              itemCopy['value'] = 'sessionDate';
            } else {
              itemCopy['value'] = '@time_block1';
            }
          }
        }
        break;
      }
      case '@time_block2': {
        if (itemCopy['granularity']) {
          if (timeGranularityParamsOpt) {
            itemCopy['value'] = timeGranularityParamsOpt.timeBlock2;
          } else {
            const millisecond = new Date(this.timePro.startDate).getTime() - new Date(this.timePro.endDate).getTime();
            const days = Math.round(Math.abs(millisecond) / (1000 * 60 * 60 * 24));
            if (days === 1) {
              itemCopy['value'] = 'sessionBlock';
            } else {
              itemCopy['value'] = '@time_block2';
            }
          }
        }
        break;
      }

      case '@child_subject_splitter': {
        if (!itemCopy['fixed']) {
          const child_splitter = this.getChildSplitter();
          itemCopy['value'] = child_splitter;
        }
        break;
      }

      case '@display_name': {
        if (!itemCopy['fixed']) {
          const displayName = this.getNodeChildDisplayName();
          itemCopy['value'] = displayName;
        }
        break;
      }

      case '@child_display_name': {
        const child_displayName = this.getNodeChildDisplayName();
        itemCopy['value'] = child_displayName;
        break;
      }

      case '@subject_splitter': {
        if (!itemCopy['fixed']) {
          itemCopy['value'] = this.selectedNode['depth'];
        }
        break;
      }
      case '@subjects': {
        if (itemCopy['type'] !== 'e') {
          if (itemCopy['exact']) {
            itemCopy['value'] = this.getExactSubject();
          } else if (itemCopy['fixed']) {
            // item['value'] = item['value'];
            // TS Error self assignment is not allowed
          } else {
            itemCopy['value'] = this.selectedNode['sbj'];
          }
        }
        break;
      }
      case '@subject_key': {
        // TODO: redundant property
        itemCopy['value'] = this.getSubjectKey();
        break;
      }
      case '@sk': {
        itemCopy['value'] = this.getSubjectKey();
        break;
      }
      case '@subject': {
        itemCopy['value'] = this.getSubjectLine();
        break;
      }
      case '@session_dates': {
        // item['value'] = this.timePro.getCurrentDate();
        itemCopy['value'] = this.timePro.getStartDateKebab();
        break;
      }
      case '@start_date': {
        itemCopy['value'] = this.timePro.getStartDateKebab();
        break;
      }
      case '@end_date': {
        itemCopy['value'] = this.timePro.getEndDateKebab(itemCopy['n']);
        break;
      }
      case '@defect_org_dim': {
        itemCopy['value'] = this.getSelectedNode()['@display_name'];
        break;
      }
      case '@dynamic_session': {
        itemCopy['value'] =
          (this.unifiedLoginService.getUserInfo() as TUserLoggedInInfo).roles.includes('AUDIT_USER') &&
          itemCopy['value'];
        break;
      }
      case '@desired_shift_time': {
        const desired_shift_time = itemCopy['value'];
        if (desired_shift_time != null) {
          itemCopy['value'] = desired_shift_time;
        }
        break;
      }
      case '@schedule_name': {
        if (this.getSelectedNode()['@schedule_name']) {
          // should be a deprecated property
          itemCopy['value'] = this.getSelectedNode()['@schedule_name'];
        }
        break;
      }
      case '@plan_name': {
        if (this.getSelectedNode()['@plan_name']) {
          itemCopy['value'] = this.getSelectedNode()['@plan_name'];
        }
        break;
      }
      case '@session_key': {
        itemCopy['value'] = [this.shiftProvider.getActiveShift().shift_id];
      }
    }

    this.checkContextArgs(itemCopy);
    return itemCopy;
  }

  public setSelectedItems(data) {
    this.selectedItems = data;
  }

  processChild() {
    if (!this.selectedChildrens || !this.selectedChildrens.length) {
      return [];
    }
    return this.selectedChildrens.map((child) => child.text);
  }

  public getSelectedItems() {
    return this.selectedItems ? this.selectedItems : [];
  }

  checkContextArgs(item) {
    return item;
  }

  getSubjectLine() {
    return this.getSubjectKey() + '-.*';
  }

  getExactSubject() {
    const sub = this.selectedNode['sbj'];
    const parts = sub.split('-');
    parts.pop();
    const fixSubject = parts.join('-');
    return fixSubject;
  }

  getOrgLevel() {
    const sub = this.selectedNode['sbj'];
    const letterNumber = /^[0-9a-zA-Z]+$/;
    let stringArr = sub.split('-');
    const len = stringArr.length;
    let org = '';

    if (len > 0) {
      stringArr = stringArr.reverse();
      stringArr.every(function (str) {
        if (str.match(letterNumber)) {
          org = str;
          return false;
        }
        return true;
      });
    }
    return org;
  }

  setSelectedChildren(children) {
    this.selectedChildrens = children;
  }

  getMilliSeconds() {
    return new Date(this.timePro.startDate).getTime() - new Date(this.timePro.endDate).getTime();
  }

  getDays(millisecond) {
    return Math.round(Math.abs(millisecond) / (1000 * 60 * 60 * 24));
  }
}
